import {
  IntegrationsService,
  OrgProfileIntegration,
  SftpUploadNodeData,
  UpdateWorkflowNodeRequest,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useStore } from '@nanostores/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEdges, useNodes } from '@xyflow/react';
import { Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';

export default function SFTPUploadConfiguration({
  setNodes,
  selectedNode,
  workflowId,
  readOnly = false,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  workflowId: string;
  readOnly?: boolean;
}) {
  const nodes = useNodes();
  const edges = useEdges();
  const wF = useStore($workflow);
  const org = useStore($org);
  const queryClient = useQueryClient();

  const [baseDirectoryPath, setBaseDirectoryPath] = useState('');
  const [searchDirectoryPath, setSearchDirectoryPath] = useState('');

  const [toDir, setToDir] = useState('');
  const [fromDir, setFromDir] = useState('');
  const [remoteFileNames, setRemoteFileNames] = useState<string[]>(['']);
  const [sftpSource, setSftpSource] = useState('');
  const [usePreviousNodeFiles, setUsePreviousNodeFiles] = useState(false);
  const [
    typeOfSearchForFilesToBeUploaded,
    setTypeOfSearchForFilesToBeUploaded,
  ] = useState('REGEX');
  const [openDirectorySelector, setOpenDirectorySelector] = useState(false);

  const [remoteFileNamesMessage, setRemoteFileNamesMessage] =
    useState(`The filenames are pre-populated based on your SFTP Download Node configuration.
             Rally will use these filenames to create a pattern and will search for
             any similar file within rally storage within the scope of workflow's execution if the SFTP download node has
             downloaded any files and will upload them to the remote directory of the SFTP server configured in this node.`);
  const [disableRemoteFileNames, setDisableRemoteFileNames] = useState(false);

  function updateSFTPConfig() {
    if (readOnly) return;
    // get the current selected node
    const newNodes = JSON.parse(JSON.stringify(nodes));

    const updatableNode = newNodes.find((n: any) => n.id === selectedNode.id);

    // find the index of selected node
    const selectedNodeIndex = newNodes.findIndex(
      (n: any) => n.id === selectedNode.id,
    );

    if (updatableNode) {
      // update the node data
      if (usePreviousNodeFiles) {
        if (sftpSource && toDir) {
          updatableNode.data = {
            ...updatableNode.data,
            sftpSource,
            toDirPath: toDir,
            usePreviousNodeFiles: usePreviousNodeFiles,
            fromDirPath: null,
            fileNamesToBeUploaded: null,
            typeOfSearchForFilesToBeUploaded: null,
          };
          newNodes[selectedNodeIndex] = updatableNode;
          setNodes(newNodes);
          return updatableNode;
        }
        return toast.error('All the configuration parameters are required!', {
          duration: 5000,
        });
      } else {
        // check how to search for local files
        if (typeOfSearchForFilesToBeUploaded === 'ALL_FILES_IN_DIRECTORY') {
          if (sftpSource && toDir) {
            updatableNode.data = {
              ...updatableNode.data,
              sftpSource,
              toDirPath: toDir,
              fromDirPath: fromDir,
              typeOfSearchForFilesToBeUploaded,
              usePreviousNodeFiles: usePreviousNodeFiles,
              fileNamesToBeUploaded: null,
            };
            newNodes[selectedNodeIndex] = updatableNode;
            setNodes(newNodes);
            return updatableNode;
          }
          return toast.error('All the configuration parameters are required!', {
            duration: 5000,
          });
        } else if (typeOfSearchForFilesToBeUploaded === 'DIRECT_FILE_NAMES') {
          if (sftpSource && toDir && remoteFileNames.length > 0) {
            updatableNode.data = {
              ...updatableNode.data,
              sftpSource,
              toDirPath: toDir,
              fileNamesToBeUploaded: remoteFileNames,
              fromDirPath: fromDir,
              typeOfSearchForFilesToBeUploaded,
              usePreviousNodeFiles: usePreviousNodeFiles,
            };
            newNodes[selectedNodeIndex] = updatableNode;
            setNodes(newNodes);
            return updatableNode;
          }
          return toast.error('All the configuration parameters are required!', {
            duration: 5000,
          });
        } else {
          if (sftpSource && toDir && remoteFileNames.length > 0) {
            updatableNode.data = {
              ...updatableNode.data,
              sftpSource,
              toDirPath: toDir,
              fileNamesToBeUploaded: remoteFileNames,
              fromDirPath: fromDir,
              typeOfSearchForFilesToBeUploaded,
              usePreviousNodeFiles: usePreviousNodeFiles,
            };
            newNodes[selectedNodeIndex] = updatableNode;
            setNodes(newNodes);
            return updatableNode;
          }
          return toast.error('All the configuration parameters are required!', {
            duration: 5000,
          });
        }
      }
    }
  }

  const sftpSources = useQuery({
    queryKey: ['getOrgIntegrations', org?.id],
    queryFn: async () => {
      if (!org) return;
      const res = await IntegrationsService.getOrgIntegrations(org?.id, 'SFTP');
      console.log('res', res);
      return res;
    },
  });

  // mutation to update a node
  const updateNode = useMutation({
    mutationFn: async (payload: {
      req: UpdateWorkflowNodeRequest;
      nodeId: string;
    }) => {
      // call the api to create a new workspace
      if (wF) {
        const res = await WorkflowsService.updateWorkflowNode(
          wF.id,
          payload.nodeId,
          payload.req,
        );
        return res;
      }
    },
    onSuccess: async data => {
      await queryClient.invalidateQueries({
        queryKey: ['getWorkflow', wF?.id],
      });
      toast.success('Saved successfully!', {
        description: 'Your changes have been saved.',
        duration: 5000,
      });
    },
    onError: error => {
      console.error(error);

      // show a toast message
      toast.error('An error occurred :: Unable to add node.', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  function isSftpUploadNodeData(data: any): data is SftpUploadNodeData {
    return data.type === 'SFTP_UPLOAD';
  }

  useEffect(() => {
    if (selectedNode) {
      if (isSftpUploadNodeData(selectedNode.data)) {
        setSftpSource(selectedNode?.data?.sftpSource || '');
        setToDir(selectedNode?.data?.toDirPath || '');
        setFromDir(selectedNode?.data?.fromDirPath || '');
        setUsePreviousNodeFiles(
          selectedNode?.data?.usePreviousNodeFiles || false,
        );
        setTypeOfSearchForFilesToBeUploaded(
          selectedNode?.data?.typeOfSearchForFilesToBeUploaded || 'REGEX',
        );

        if (
          selectedNode?.data?.fileNamesToBeUploaded &&
          Array.isArray(selectedNode?.data?.fileNamesToBeUploaded) &&
          selectedNode?.data?.fileNamesToBeUploaded?.length > 0
        ) {
          setRemoteFileNames(selectedNode?.data?.fileNamesToBeUploaded);
        }
      }
    }
  }, [selectedNode]);

  return (
    <div className="h-full w-[100%] overflow-y-auto px-1">
      <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
        <div className="flex items-center">
          <Label htmlFor="email">SFTP source </Label>

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[300px] text-xs">
                  Indicates the SFTP server where the data will be uploaded.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Select
          onValueChange={value => {
            if (!readOnly) {
              // update the data ingestion node configuration
              setSftpSource(value);
            }
          }}
          value={sftpSource} // @NOTE: Need to fix the types later on
          disabled={readOnly}
        >
          <SelectTrigger className={`w-[100%] ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}>
            <SelectValue placeholder="Select a SFTP source" />
          </SelectTrigger>
          <SelectContent className="w-[100%]">
            <SelectGroup>
              <SelectLabel>SFTP Sources</SelectLabel>
              {sftpSources?.data?.map(
                (source: OrgProfileIntegration, s: number) => (
                  <SelectItem value={source.id} key={s}>
                    {source.name}
                  </SelectItem>
                ),
              )}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
        <div className="flex items-center">
          <Label htmlFor="email">Remote directory path </Label>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="w-[300px] text-xs">
                  This field specifies the directory on the SFTP server to which
                  files will be uploaded. Please ensure the correct directory
                  path is provided.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          type="text"
          id="email"
          placeholder="/home/abc/project1"
          className={`italic ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}
          value={toDir}
          disabled={readOnly} // ✅ Disable input in read-only mode
          onChange={e => {
            if (!readOnly) { // ✅ Prevent updates
              setToDir(e.target.value);
            }
          }}
        />

      </div>

      <div className="mt-4 flex items-center space-x-2">
        <Switch
          id="airplane-mode"
          checked={usePreviousNodeFiles}
          disabled={readOnly}
          onCheckedChange={value => {
            // console.log('vavava', value);
            if (!readOnly) {
              const edgesToSelectedNode = edges.find(
                edge => edge?.target === selectedNode.id,
              );

              if (edgesToSelectedNode) {
                const previousNode = nodes.find(
                  node => node.id === edgesToSelectedNode?.source,
                );

                if (previousNode?.type !== 'SFTP_DOWNLOAD') {
                  toast.error(
                    'The previous node must be a SFTP Download node to use its files',
                    {
                      duration: 5000,
                    },
                  );
                  return;
                }

                if (
                  Array.isArray(previousNode?.data?.fileNames) &&
                  previousNode?.data?.fileNames?.length === 0
                ) {
                  toast.error(
                    'Cannot find the configuration of file names in the previous node!',
                    {
                      duration: 5000,
                    },
                  );
                  return;
                }

                if (
                  Array.isArray(previousNode?.data?.fileNames) &&
                  previousNode?.data?.fileNames?.length > 0
                ) {
                  setRemoteFileNames(previousNode?.data?.fileNames);
                }
              }
              setUsePreviousNodeFiles(value);
              console.log('nodes', nodes);
              console.log('selectedNode', selectedNode);
            }
          }}
        />
        <Label htmlFor="airplane-mode">
          Use previous SFTP Download Node files
        </Label>
      </div>

      {!usePreviousNodeFiles && (
        <>
          <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
            <div className="flex items-center">
              <Label htmlFor="email">Local directory path</Label>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="w-[300px] text-xs">
                      This field specifies the directory within Rally from where
                      the files will be uploaded. Please ensure the correct
                      directory path is provided. You can view your files in the
                      "Storage" section of the workflow.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              type="text"
              id="email"
              placeholder="/home/abc/project1"
              className="italic"
              value={fromDir}
              disabled={readOnly} // ✅ Disable input in read-only mode
              onChange={e => {
                if (!readOnly) { // ✅ Prevent updates
                  setFromDir(e.target.value);
                }
              }}
            />
          </div>
          <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
            <div className="flex items-center">
              <Label>Files to upload</Label>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="ml-1 h-3 w-3 text-muted-foreground hover:cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="w-[300px] text-xs">
                      This field specifies the criteria for how Rally should
                      search for files in rally storage and upload the files to
                      the configured SFTP source. Please select the desired
                      search pattern.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <RadioGroup
              defaultValue="DIRECT_FILE_NAMES"
              value={typeOfSearchForFilesToBeUploaded}
              disabled={readOnly}
              onValueChange={(value: string) => {
                if (!readOnly) {
                  if (value === 'DIRECT_FILE_NAMES') {
                    setRemoteFileNames(['']);
                  } else {
                    setRemoteFileNames(['', '', '']);
                  }
                  setTypeOfSearchForFilesToBeUploaded(value);
                }
              }}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="REGEX" id="r1" />
                <Label htmlFor="r1">
                  Use Regex to search files to be uploaded
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="DIRECT_FILE_NAMES" id="r2" />
                <Label htmlFor="r2">
                  Use direct file names to search files to be uploaded
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ALL_FILES_IN_DIRECTORY" id="r2" />
                <Label htmlFor="r2">
                  Upload all the files in the specified directory
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* <Button
            variant="outline"
            size="icon"
            className="ml-1"
            onClick={() => {
              setOpenDirectorySelector(true);
            }}
          >
            <Plus className="h-4 w-4" /> sel dr
          </Button> */}
          {/* <DirectorySelector
            open={openDirectorySelector}
            setOpen={setOpenDirectorySelector}
            baseDirectoryPath={baseDirectoryPath}
            searchDirectoryPath={searchDirectoryPath}
            setSearchDirectoryPathPath={setSearchDirectoryPath}
          /> */}
          {typeOfSearchForFilesToBeUploaded !== 'ALL_FILES_IN_DIRECTORY' && (
            <div className="mt-4 grid w-full max-w-sm items-center gap-2.5">
              <Label>Remote file names</Label>
              {remoteFileNames?.map((fileName, i) => (
                <div className="flex items-center" key={i}>
                  <Input
                    type="text"
                    id="email"
                    placeholder="call_records_01.csv"
                    className={`italic ${readOnly ? "bg-gray-200 text-gray-500 cursor-not-allowed" : ""}`}
                    value={fileName}
                    disabled={readOnly}
                    onChange={e => {
                      if (!readOnly) {
                        const updatedFileNames: string[] = [...remoteFileNames];
                        updatedFileNames[i] = e.target.value;
                        setRemoteFileNames(updatedFileNames);
                      }
                    }
                    }
                    key={i}
                  />
                  {typeOfSearchForFilesToBeUploaded === 'DIRECT_FILE_NAMES' && (
                    <>
                      {remoteFileNames?.length > 1 && (
                        <Button
                          variant="outline"
                          size="icon"
                          className="ml-1"
                          onClick={() => {
                            if (!readOnly) {
                              const updatedFileNames: string[] = [
                                ...remoteFileNames,
                              ];
                              updatedFileNames.splice(i, 1);
                              setRemoteFileNames(updatedFileNames);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="icon"
                        className="ml-1"
                        onClick={() => {
                          if (!readOnly) {
                            setRemoteFileNames([...remoteFileNames, '']);
                          }
                        }
                        }
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              ))}

              <div className="mt-0 flex w-full max-w-sm gap-1.5 rounded-md bg-slate-200 px-2 py-2 text-xs">
                <div className="flex">
                  <Info className="mr-1 mt-0.5 h-3 w-3 text-muted-foreground" />
                </div>
                <div className="flex">
                  {typeOfSearchForFilesToBeUploaded === 'REGEX' && (
                    <p>
                      Rally will use regular expressions to search for similar
                      files within rally storage and upload them to the
                      configured SFTP source.
                    </p>
                  )}

                  {typeOfSearchForFilesToBeUploaded === 'DIRECT_FILE_NAMES' && (
                    <p>
                      Rally will only search for the specified files within
                      rally storage and upload them to the configured SFTP
                      source.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-4 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          disabled={readOnly || updateNode.isPending}
          onClick={async () => {
            if (!readOnly) {
              const updatableNode = updateSFTPConfig();
              if (updatableNode) {
                await updateNode.mutateAsync({
                  req: { data: { ...updatableNode.data, type: 'SFTP_UPLOAD' } },
                  nodeId: updatableNode?.id,
                });
              }
            }
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
